import { Card, Col, Row, Button } from "react-bootstrap";
import { useContext, useState } from "react";
import Physician from "../../../Physician/Models/Physician";
import { Certificate } from "../../../Physician/Models/Certificate";
import { abpnClient } from "../../../../Services/AbpnClient";
import { AuthContext } from "../../../Auth/AuthProvider";
import AbpnSpinner from "../../../Shared/Components/AbpnSpinner";
import { CertificateRow } from "./CertificateRow";
import { CCBlock } from "../../../Physician/Models/CCBlock";
import { deepCopy } from "../../../Shared/Utility/DeepCopy";
import { getDateWithOffset } from "../../../Shared/Utility/GetDateWithOffset";
import RequestCertificationVerification from "./RequestCertificationVerification";
import ClinicalStatusRow from "./ClinicalStatusRow";
import React from "react";

interface IProps {
    physician: Physician;
    updatePhysician: (updatedPhysician: Physician) => void;
}

export default function CertificatesCard(props: IProps) {
    const authContext = useContext(AuthContext);

    let groupedCertsBySpecialty: Record<number, Array<Certificate>> = {};
    groupedCertsBySpecialty = props.physician.certificates.reduce(
        (result, item) => ({
            ...result,
            [item["certificationId"]]: [
                ...(result[item["certificationId"]] || []),
                item,
            ],
        }),
        groupedCertsBySpecialty
    );

    const [groupedCertificates] = useState(groupedCertsBySpecialty);
    const [isDownloading, setIsDownloading] = useState(false);

    const hadCertificate = props.physician.certificates?.length > 0;

    const evaluateCurrentBlockAndCallUpdate = async (physician : Physician) => {
        if (props.physician.currentCCEnrollment == null) return;

        const ccBlock = await abpnClient.get<CCBlock>("/api/physician/EvaluateCurrentBlockRequirements", authContext);
        const certificates = await abpnClient.get<Array<Certificate>>("/api/physician/GetCertificates", authContext);

        var index = physician.currentCCEnrollment.ccBlocks.findIndex(_ => _.ccBlockId === ccBlock.ccBlockId);
        physician.currentCCEnrollment.ccBlocks.splice(index, 1, ccBlock);
        physician.currentCCEnrollment.currentCCBlock = ccBlock;

        physician.certificates = certificates;
        props.updatePhysician(physician);
    } 

    const onSaveCertificate = (updatedCertificate: Certificate) => {
        let updatedPhysician = deepCopy(props.physician);
        var certIndex = updatedPhysician.certificates.findIndex(_ => _.certificateId == updatedCertificate.certificateId);
        updatedPhysician.certificates[certIndex] = updatedCertificate;
        evaluateCurrentBlockAndCallUpdate(updatedPhysician);
    }

    let sortedCertificates = Object.values(groupedCertificates).sort((a: Certificate[], b: Certificate[]) => {
        //sort by Specialty/Subspecialty
        if (a[0].isPrimary === b[0].isPrimary) {
            //then by recent Award Date
            let aAwardDates = a.map(_ => getDateWithOffset(_.awardDate).getTime());
            let bAwardDates = b.map(_ => getDateWithOffset(_.awardDate).getTime());
            return Math.max(...aAwardDates) - Math.max(...bAwardDates);
        } else {
            return a[0].isPrimary ? -1 : 1
        }
    });

    const download = async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();

        setIsDownloading(true);

        var response = await abpnClient.getResponse("/api/physician/cccertificate", authContext)
        var blob = await response.blob();

        const downloadFileUrl = window.URL.createObjectURL(
            new Blob([blob]),
        );
        const link = document.createElement('a');
        link.href = downloadFileUrl;
        link.setAttribute(
            'download',
            `CertificateStatus.pdf`,
        );

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode?.removeChild(link);

        setIsDownloading(false);
    }

    let certificatesRows = sortedCertificates.map((certificates, index) => <CertificateRow key={index} physician={props.physician} certificateGroup={certificates} onSave={onSaveCertificate} />);

    let showPrint = props.physician.currentCCEnrollment && !props.physician.currentCCEnrollment.isBehindCurrentBlock;

    let nextBlock = props.physician.currentCCEnrollment?.ccBlocks?.find(_ => getDateWithOffset(_.startDate) > getDateWithOffset(props.physician.currentCCEnrollment?.currentCCBlock?.endDate));

    return (
        <Card className="mb-4" border="pp-secondary-grey">
            <Card.Body>
                <Card.Title className="mb-0 font-weight-bolder">
                    <span className="text-pp-primary">Certifications</span>
                </Card.Title>
                { props.physician.currentCCEnrollment?.currentCCBlock &&
                <ClinicalStatusRow ccBlock={nextBlock ? nextBlock : props.physician.currentCCEnrollment?.currentCCBlock} physician={props.physician} updatePhysician={props.updatePhysician} editable={false}/>
                }
                <hr />
                <Row className="mt-3">
                    <Col>
                        {certificatesRows.length > 0 ? certificatesRows : "Not certified"}
                        {showPrint &&
                            <Row>
                                <Col>
                                    {isDownloading ? <AbpnSpinner /> :
                                        <Button variant="pp-primary" onClick={(e) => download(e)} className="m-1 w-100">Download Continuing Certification Status Certificate</Button>}
                                </Col>
                            </Row>
                        }
                    </Col>
                </Row>
                <RequestCertificationVerification hadCertificate={hadCertificate}/>
            </Card.Body>
        </Card>
    );
}